import {
    v4
} from 'uuid';
import localeCode from 'locale-code';
import {
    post
} from '../../networking';
import spacetime from 'spacetime';
import { CustomDataDto, normalizeCountry, normalizeEmail, normalizeGender, normalizeName, normalizePhone, sha256Hash, UserData } from './utils';
import dataDogLogger from '../datadog';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export enum MetaPixelAudience {
    DIGITAL = 'digital',
    MEDS = 'meds'
}

export enum FACEBOOK_PIXEL_ID {
    DIGITAL = '276794426891651',
    MEDS = '694028303037686',
    MEDS_2 = '939807765230766'
}

export enum MetaTrackingTypeEnum {
    TRACK = 'track',
    TRACK_SINGLE = 'trackSingle',
    TRACK_SINGLE_CUSTOM = 'trackSingleCustom'
}

export enum MetaTrackingEventName {
    PAGE_VIEW = 'PageView',
    COMPLETE_REGISTRATION = 'CompleteRegistration',
    START_NO_CC_TRIAL_DIRECT = 'StartNoCCTrialDirect',
    START_TRIAL = 'StartTrial',
    SUNNYSIDE_START_TRIAL = 'SunnysideTrialStart',
    PURCHASE = 'Purchase',
    SUNNYSIDE_M_2 = 'Sunnyside_M_2', // M2 - Med provider review checkout success event
    SUNNYSIDE_M_1 = 'Sunnyside_M_1' // M1 - Med paid subscription event
}

type FacebookTrackingUserData = UserData & {
    db: string,
    fbc?: string,
    fbp?: string
}

export type FacebookTrackingParams = {
    event_name: MetaTrackingEventName,
    event_id: string,
    event_time: number,
    action_source: string,
    event_source_url: string,
    user_data: FacebookTrackingUserData,
    custom_data?: CustomDataDto,
    isNewMedPixel: boolean,
    pixel_audience: MetaPixelAudience
}

const normalizeDOB = (dob?: string): string | undefined => {
    if (!dob) return undefined;
    const spacetimeRefillDate = spacetime(dob);
    return spacetimeRefillDate?.format('iso-short')?.toString()?.replace(/-/g,'') || undefined;
};

function fbqProxy(
    event_name: string,
    params: FacebookTrackingUserData,
    event_id: string,
    isNewMedPixel: boolean,
    pixel_audience: MetaPixelAudience,
    trackingType: MetaTrackingTypeEnum
) {
    const fbq = window && window['fbq'];
    if(typeof fbq !== 'function') {
        dataDogLogger('error', `Meta Pixel not initialized when firing event ${event_name}`);
        return;
    }

    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        // log to console and return
        console.log('Meta Pixel', event_name, event_id, params);
        return;
    }

    if(pixel_audience === MetaPixelAudience.DIGITAL) {
        fbq(
            trackingType,
            FACEBOOK_PIXEL_ID.DIGITAL,
            event_name,
            params,
            {
                eventID: event_id
            },
        );
    } else if (pixel_audience === MetaPixelAudience.MEDS) {
        const medPixelId = isNewMedPixel ? FACEBOOK_PIXEL_ID.MEDS_2 : FACEBOOK_PIXEL_ID.MEDS;
        fbq(
            trackingType,
            medPixelId,
            event_name,
            params,
            {
                eventID: event_id
            },
        );
    }
}

async function cutbackFbProxy(event_name: string, params: FacebookTrackingParams) {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        // log to console and return
        console.log('Meta CAPI', event_name, params.event_id, params);
        return;
    }

    // post
    await post('tracking/fb', params);
}

export default async function metaEvent({
    event_name,
    user,
    url,
    cookies,
    custom_data,
    isNewMedPixel = false,
    pixel_audience = MetaPixelAudience.DIGITAL,
    trackingType = MetaTrackingTypeEnum.TRACK_SINGLE
}: {
    event_name: MetaTrackingEventName,
    user: any,
    url: string,
    cookies?: any,
    custom_data?: CustomDataDto,
    isNewMedPixel?: boolean,
    pixel_audience?: MetaPixelAudience,
    trackingType?: MetaTrackingTypeEnum
}) {
    if (!user) {
        return;
    }

    const event_id = v4();
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const countryCode = localeCode.getCountryCode(locale);

    const user_data: FacebookTrackingUserData = {
        em: normalizeEmail(user.email),
        ph: normalizePhone(user.phone),
        fn: normalizeName(user.givenName),
        ln: normalizeName(user.familyName),
        db: normalizeDOB(user.birthDate),
        ge: normalizeGender(user.gender),
        external_id: user.id,
        country: normalizeCountry(countryCode)
    };

    // insert fpc and fbp if they exist
    if(cookies) {
        if (cookies['_fbc']) {
            user_data.fbc = cookies['_fbc'];
        }
        if (cookies['_fbp']) {
            user_data.fbp = cookies['_fbp'];
        }
    }

    fbqProxy(event_name, user_data, event_id, isNewMedPixel, pixel_audience, trackingType);

    // clone so we can hash data
    const server_user_data: FacebookTrackingUserData = {
        ...user_data
    };

    // hash required keys to send to server, required by FB:
    // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
    ['em', 'ph', 'fn', 'ln', 'db', 'ge', 'country'].forEach(key => {
        if(!server_user_data[key]) {
            return;
        }
        const data: string = server_user_data[key] as string;
        server_user_data[key] = sha256Hash(data);
    });

    server_user_data.client_user_agent = window.navigator.userAgent,

    // send to our internal server
    await cutbackFbProxy(event_name, {
        event_name: event_name,
        event_id: event_id,
        event_time: Math.floor(Date.now() / 1000), // server expects seconds
        action_source: 'website',
        user_data: server_user_data,
        event_source_url: url,
        custom_data: custom_data,
        isNewMedPixel,
        pixel_audience: pixel_audience
    });
}
